import { useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Share2, Instagram, Copy } from "lucide-react";

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.4c1.5.8 3.1 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.1.8.8-3-.2-.3C4 15 3.7 13.5 3.7 12c0-4.6 3.7-8.3 8.3-8.3s8.3 3.7 8.3 8.3-3.7 8.2-8.3 8.2z" /></svg>
);

const dataUrlToFile = async (dataUrl, filename) => {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], filename, { type: "image/png" });
};

export default function ShareButtons({ text, url, getImage, name, compact = false }) {
  const [busy, setBusy] = useState(null);
  const full = url ? `${text}\n\n${url}` : text;
  const enc = encodeURIComponent(full);
  const fileName = `VoteForMe_${(name || "voice-card").replace(/\s+/g, "_")}.png`;
  const canNative = typeof navigator !== "undefined" && !!navigator.share;

  const downloadImage = (dataUrl) => {
    const a = document.createElement("a");
    a.download = fileName; a.href = dataUrl; a.click();
  };

  const shareNative = async () => {
    setBusy("native");
    try {
      const payload = { title: "Vote for Me Until I Can", text: full };
      if (getImage) {
        const dataUrl = await getImage();
        if (dataUrl) {
          const file = await dataUrlToFile(dataUrl, fileName);
          if (navigator.canShare && navigator.canShare({ files: [file] })) payload.files = [file];
        }
      }
      await navigator.share(payload);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing was cancelled");
    } finally { setBusy(null); }
  };

  const shareInstagram = async () => {
    setBusy("ig");
    try {
      const dataUrl = getImage ? await getImage() : null;
      if (dataUrl && canNative && navigator.canShare) {
        const file = await dataUrlToFile(dataUrl, fileName);
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: full });
          return;
        }
      }
      if (dataUrl) downloadImage(dataUrl);
      await navigator.clipboard.writeText(full);
      toast.success("Card saved + caption copied! Open Instagram and post it from your photos.", { duration: 6000 });
      window.open("https://www.instagram.com/", "_blank", "noopener");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not prepare Instagram share");
    } finally { setBusy(null); }
  };

  const copyAll = async () => {
    await navigator.clipboard.writeText(full);
    toast.success("Message + link copied ✓");
  };

  return (
    <div className={`share-row ${compact ? "compact" : ""}`} data-testid="share-buttons">
      <a className="share-btn sms" href={`sms:?&body=${enc}`} data-testid="share-sms-btn"><MessageSquare size={16} /><span>Text</span></a>
      <a className="share-btn wa" href={`https://wa.me/?text=${enc}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-btn"><WhatsAppIcon /><span>WhatsApp</span></a>
      <button className="share-btn ig" onClick={shareInstagram} disabled={busy === "ig"} data-testid="share-instagram-btn"><Instagram size={16} /><span>{busy === "ig" ? "Preparing…" : "Instagram"}</span></button>
      {canNative
        ? <button className="share-btn more" onClick={shareNative} disabled={busy === "native"} data-testid="share-native-btn"><Share2 size={16} /><span>More…</span></button>
        : <button className="share-btn more" onClick={copyAll} data-testid="share-copy-btn"><Copy size={16} /><span>Copy</span></button>}
    </div>
  );
}
